import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { INTEGRATION_PRESETS, ENTITY_LABELS } from "@/lib/integration-presets";
import { Plug, Loader2, Trash2, RefreshCw, Send, Copy } from "lucide-react";

// The integration tables are new and not yet in the generated types.
const db = supabase as any;

interface Endpoint {
  id: string; name: string; preset: string; url: string; api_key: string | null; company_id: string | null;
  secret: string; enabled: boolean; entities: string[]; last_status: string | null; last_delivery_at: string | null;
}

export default function Integrations() {
  const { toast } = useToast();
  const [items, setItems] = useState<Endpoint[]>([]);
  const [pending, setPending] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [preset, setPreset] = useState(INTEGRATION_PRESETS[0].id);
  const [url, setUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const current = INTEGRATION_PRESETS.find((p) => p.id === preset)!;

  const load = useCallback(async () => {
    const { data } = await db.from("integration_endpoints").select("*").order("created_at");
    setItems((data as Endpoint[]) || []);
    const { data: out } = await db.from("integration_outbox").select("endpoint_id").eq("status", "pending").limit(1000);
    const counts: Record<string, number> = {};
    (out || []).forEach((r: any) => { counts[r.endpoint_id] = (counts[r.endpoint_id] || 0) + 1; });
    setPending(counts);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setUrl(current.defaultUrl); }, [current]);

  const test = async (id: string) => {
    const { data, error } = await supabase.functions.invoke("integration-dispatch", { body: { action: "test", endpoint_id: id } });
    const ok = !error && data?.success;
    toast({ title: ok ? "Ligação OK" : "Falha na ligação", description: ok ? undefined : (data?.error || error?.message), variant: ok ? undefined : "destructive" });
    return ok;
  };

  // One click: create → test ping → send all existing data.
  const connect = async () => {
    if (!/^https?:\/\//i.test(url)) { toast({ title: "Indique o URL de receção (https://…)", variant: "destructive" }); return; }
    setBusy("new");
    const { data, error } = await db.from("integration_endpoints").insert({
      name: current.name, preset, url: url.trim(), api_key: apiKey.trim() || null, company_id: companyId.trim() || null,
    }).select().single();
    if (error || !data) { toast({ title: "Erro ao criar", description: error?.message, variant: "destructive" }); setBusy(null); return; }
    const ok = await test(data.id);
    if (ok) {
      const { data: n, error: e2 } = await db.rpc("integration_backfill", { _endpoint_id: data.id });
      toast({ title: e2 ? "Ligado, mas a sincronização falhou" : `Sincronização iniciada (${n} registos em fila)`, description: e2?.message, variant: e2 ? "destructive" : undefined });
    }
    setApiKey(""); setCompanyId(""); setBusy(null); load();
  };

  const patch = async (id: string, values: Partial<Endpoint>) => { await db.from("integration_endpoints").update({ ...values, updated_at: new Date().toISOString() }).eq("id", id); load(); };
  const remove = async (id: string) => { if (!confirm("Remover esta integração? A fila pendente também é apagada.")) return; await db.from("integration_endpoints").delete().eq("id", id); load(); };
  const resync = async (id: string) => {
    setBusy(id);
    const { data, error } = await db.rpc("integration_backfill", { _endpoint_id: id });
    toast({ title: error ? "Erro" : `${data} registos em fila`, description: error?.message, variant: error ? "destructive" : undefined });
    setBusy(null); load();
  };
  const copy = (text: string) => { navigator.clipboard?.writeText(text); toast({ title: "Copiado" }); };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Plug className="w-6 h-6" /> Integrações</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Tudo o que acontece na Mente Sã (agendamentos, vendas, colaboradores, clientes, finanças) é enviado, assinado e com reenvio automático, para os sistemas ligados aqui.
        </p>
      </div>

      <section className="rounded-xl border p-5 space-y-4">
        <h2 className="font-semibold">Ligar novo sistema</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Sistema</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{INTEGRATION_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{current.description}</p>
          </div>
          <div><Label>URL de receção (https)</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://app.bebrave.com/api/webhooks/mentesa" /></div>
          <div><Label>Chave / token do sistema (opcional)</Label><Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" /></div>
          {current.needsCompanyId && <div><Label>ID da empresa no {current.name}</Label><Input value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="ex.: mentesa" /></div>}
        </div>
        <Button onClick={connect} disabled={busy === "new"} className="gap-2">
          {busy === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Conectar e sincronizar tudo
        </Button>
      </section>

      {items.map((ep) => (
        <section key={ep.id} className="rounded-xl border p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold">{ep.name} {ep.company_id && <span className="text-muted-foreground font-normal">· {ep.company_id}</span>}</h3>
              <p className="text-xs text-muted-foreground break-all">{ep.url}</p>
              <p className="text-xs mt-1">Estado: {ep.last_status || "—"} · Na fila: {pending[ep.id] || 0}{ep.last_delivery_at && ` · Último envio: ${new Date(ep.last_delivery_at).toLocaleString("pt-PT")}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={ep.enabled} onCheckedChange={(v) => patch(ep.id, { enabled: v })} />
              <Button size="sm" variant="outline" onClick={() => test(ep.id).then(load)}><Send className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" disabled={busy === ep.id} onClick={() => resync(ep.id)} title="Reenviar tudo"><RefreshCw className="w-4 h-4" /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(ep.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {Object.entries(ENTITY_LABELS).map(([k, label]) => (
              <label key={k} className="text-xs flex items-center gap-1.5">
                <input type="checkbox" checked={ep.entities.includes(k)} onChange={(e) => patch(ep.id, { entities: e.target.checked ? [...ep.entities, k] : ep.entities.filter((x) => x !== k) })} />
                {label}
              </label>
            ))}
          </div>
          <div className="text-xs flex items-center gap-2">
            <span className="text-muted-foreground">Segredo de assinatura (configure no {ep.name}):</span>
            <Button size="sm" variant="ghost" className="h-6 gap-1" onClick={() => copy(ep.secret)}><Copy className="w-3 h-3" /> copiar</Button>
          </div>
        </section>
      ))}
    </div>
  );
}
